import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import {
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  X,
  Gauge,
  CalendarDays,
  Trophy,
  Sparkles,
  BarChart3,
  Activity,
  Leaf,
  CloudRain,
  Zap,
  Flame,
  EyeOff,
  Scale,
  Smile,
  HeartCrack,
  Eye,
  User,
  Frown as FrownIcon,
  CircleSlash,
  Cloud,
  Loader2,
  Send,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  Users,
  UserX,
  Clock,
  Check,
  Copy,
  Info
} from "lucide-react";
import { useTheme } from "../../context/themeContext";
import { useAuth } from "../../context/authContext";
import serverUrl from "../../utils/utilsser";
import dayjs from "dayjs";
import { DEMO_DATA, demoMoodFetch } from "../../utils/demoData";

// ---------------------------------------------------------------------------
// Infiheal brand tokens — identical system to TestAnalyticsPage / Home / Sidebar
// so this section drops in seamlessly and follows the same dark/light theme.
// ---------------------------------------------------------------------------

const BRAND = {
  gradient: "linear-gradient(90deg, #ef5da8 0%, #8b7cf6 100%)",
  navy: "#3d3a6b",
  teal: "#34d9b8",
  pink: "#ef5da8",
  purple: "#8b7cf6",
  gold: "#f6ad3c",
  silver: "#9aa3b5",
  bronze: "#c98a4b",
};

const TOKENS = {
  dark: {
    isDark: true,
    // Same soft radial-gradient wash as Home — no flat background color of
    // its own, so it inherits the app shell's background and stays visually
    // consistent with the rest of the dashboard instead of looking like a
    // separate, boxed-off page.
    pageWash:
      "bg-[radial-gradient(circle_at_15%_0%,rgba(139,124,246,0.10),transparent_45%),radial-gradient(circle_at_85%_10%,rgba(239,93,168,0.08),transparent_40%)]",
    card: "bg-[#13152c]/90 border-white/[0.06]",
    cardShadow:
      "shadow-[0_1px_0_rgba(255,255,255,0.04)_inset,0_20px_50px_-20px_rgba(0,0,0,0.65)]",
    textPrimary: "text-white",
    textMuted: "text-slate-400",
    textFaint: "text-slate-500",
    surface: "bg-white/[0.04]",
    surfaceSoft: "bg-white/[0.03]",
    surfaceHover: "hover:bg-white/[0.06]",
    pillBorder: "border-white/10",
    gridStroke: "rgba(255,255,255,0.07)",
    axisTick: "#8790ad",
    tooltipBg: "#1d2144",
    tooltipBorder: "rgba(255,255,255,0.1)",
    overlay: "bg-black/60",
  },
  light: {
    isDark: false,
    pageWash:
      "bg-[radial-gradient(circle_at_10%_-5%,rgba(239,93,168,0.08),transparent_42%),radial-gradient(circle_at_90%_0%,rgba(139,124,246,0.10),transparent_42%)]",
    card: "bg-white/95 border-black/[0.06]",
    cardShadow:
      "shadow-[0_1px_0_rgba(255,255,255,0.6)_inset,0_20px_44px_-24px_rgba(31,41,74,0.22)]",
    textPrimary: "text-slate-900",
    textMuted: "text-slate-500",
    textFaint: "text-slate-400",
    surface: "bg-black/[0.035]",
    surfaceSoft: "bg-black/[0.03]",
    surfaceHover: "hover:bg-black/[0.045]",
    pillBorder: "border-black/10",
    gridStroke: "rgba(15,23,42,0.08)",
    axisTick: "#7c8299",
    tooltipBg: "#ffffff",
    tooltipBorder: "rgba(15,23,42,0.08)",
    overlay: "bg-slate-900/50",
  },
};

const ACCENT_GLOW = {
  purple: {
    dark: "hover:shadow-[0_24px_60px_-18px_rgba(139,124,246,0.45)] hover:border-[#8b7cf6]/30",
    light:
      "hover:shadow-[0_24px_50px_-20px_rgba(139,124,246,0.35)] hover:border-[#8b7cf6]/30",
  },
  teal: {
    dark: "hover:shadow-[0_24px_60px_-18px_rgba(52,217,184,0.4)] hover:border-[#34d9b8]/30",
    light:
      "hover:shadow-[0_24px_50px_-20px_rgba(52,217,184,0.3)] hover:border-[#34d9b8]/30",
  },
  pink: {
    dark: "hover:shadow-[0_24px_60px_-18px_rgba(239,93,168,0.4)] hover:border-[#ef5da8]/30",
    light:
      "hover:shadow-[0_24px_50px_-20px_rgba(239,93,168,0.3)] hover:border-[#ef5da8]/30",
  },
};

// ---------------------------------------------------------------------------
// Small responsive helper — lets us trim chart/axis details on narrow
// (phone-width) screens without touching anything on desktop. Pure JS
// (no Tailwind) because Recharts props (fontSize, margins) can't be
// controlled with CSS classes.
// ---------------------------------------------------------------------------

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  return isMobile;
}

// ---------------------------------------------------------------------------
// Mood scale — the API reports avgMood / organizationAvg as a 0–100 score,
// not a 1–5 score, so all thresholds below work on that percentage scale.
// ---------------------------------------------------------------------------

function moodMeta(score) {
  const s = score ?? 0;
  if (s >= 80) return { emoji: "😄", label: "Excellent" };
  if (s >= 60) return { emoji: "🙂", label: "Happy" };
  if (s >= 40) return { emoji: "😐", label: "Okay" };
  if (s >= 20) return { emoji: "🙁", label: "Low" };
  return { emoji: "😟", label: "Very Low" };
}

// Distress/emotion-intensity scores run the other way round from mood: a
// LOW number is the good outcome here, since these track how strongly an
// emotion like sadness or loneliness is showing up in conversations.
function severityMeta(score) {
  const s = score ?? 0;
  if (s >= 66) return { emoji: "😟", label: "High", tint: BRAND.pink };
  if (s >= 33) return { emoji: "😐", label: "Medium", tint: BRAND.gold };
  return { emoji: "🙂", label: "Low", tint: BRAND.teal };
}

function shortDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function shortMonth(monthStr) {
  const [y, m] = monthStr.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("en-US", { month: "short" });
}

// ---------------------------------------------------------------------------
// Shared building blocks
// ---------------------------------------------------------------------------

function CardShell({ children, className = "", accent = "purple" }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const glow = ACCENT_GLOW[accent][isDark ? "dark" : "light"];
  return (
    <div
      className={
        "rounded-2xl sm:rounded-3xl border p-4 sm:p-5 md:p-6 backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 " +
        t.card +
        " " +
        t.cardShadow +
        " " +
        glow +
        " " +
        className
      }
    >
      {children}
    </div>
  );
}

function LaurelLeaf({ flip = false, color }) {
  return (
    <Leaf
      size={15}
      strokeWidth={2}
      style={{
        color,
        opacity: 0.55,
        transform: flip ? "scaleX(-1) rotate(15deg)" : "rotate(-15deg)",
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Box — average-score card (This Month / Today)
// ---------------------------------------------------------------------------

function AvgScoreCard({ label, sublabel, score, icon: Icon, tint, accent }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const meta = moodMeta(score);
  const hasScore = score != null && !Number.isNaN(score);

  return (
    <CardShell accent={accent}>
      <div className="mb-4 flex items-center justify-between">
        <span
          className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full"
          style={{ backgroundColor: `${tint}22`, color: tint }}
        >
          <Icon size={18} strokeWidth={2} />
        </span>
        <span
          className={
            "rounded-full border px-2.5 py-1 sm:px-3 text-[10px] sm:text-[11px] font-medium " +
            t.pillBorder +
            " " +
            t.surfaceSoft +
            " " +
            t.textMuted
          }
        >
          {sublabel}
        </span>
      </div>

      <div
        className={
          "text-xs font-semibold uppercase tracking-wide " + t.textMuted
        }
      >
        {label}
      </div>

      <div className="mt-2 flex items-center gap-2">
        <LaurelLeaf color={tint} />
        <span
          className={
            "text-3xl sm:text-4xl font-extrabold leading-none " + t.textPrimary
          }
        >
          {hasScore ? Math.round(score) : "—"}
        </span>
        {hasScore && (
          <span className={"text-lg font-semibold " + t.textFaint}>%</span>
        )}
        <LaurelLeaf flip color={tint} />
      </div>

      <div className={"mt-3 flex items-center gap-1.5 text-sm " + t.textMuted}>
        {hasScore ? (
          <>
            <span>{meta.emoji}</span>
            Average mood is trending{" "}
            <span className={"font-semibold " + t.textPrimary}>
              {meta.label}
            </span>
          </>
        ) : (
          "No mood data yet for this period."
        )}
      </div>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// Box — Mood statistics card, with a working Today / Yesterday / This Week
// dropdown. Only options backed by real data are shown.
// ---------------------------------------------------------------------------

function MoodStatisticsCard({ options }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(0);

  const validOptions = options.filter(
    (o) => o.value != null && !Number.isNaN(o.value),
  );
  const current = validOptions[selected] ?? validOptions[0];
  const score = current?.value;
  const pct = score != null ? Math.round(score) : null;
  const meta = moodMeta(score);

  if (validOptions.length === 0) {
    return (
      <CardShell accent="teal">
        <h2 className={"mb-2 text-lg font-bold " + t.textPrimary}>
          Mood Statistics
        </h2>
        <p className={"text-sm " + t.textMuted}>No mood data available yet.</p>
      </CardShell>
    );
  }

  return (
    <CardShell accent="teal" className="relative">
      <div className="mb-6 flex items-center justify-between gap-2">
        <h2 className={"text-base sm:text-lg font-bold " + t.textPrimary}>
          Mood Statistics
        </h2>

        <div className="relative shrink-0">
          <button
            onClick={() => setOpen((v) => !v)}
            className={
              "flex items-center gap-1 rounded-full border px-2.5 py-1.5 sm:px-3 text-[11px] sm:text-xs font-medium transition-colors " +
              t.pillBorder +
              " " +
              t.surfaceSoft +
              " " +
              t.textMuted +
              " " +
              t.surfaceHover
            }
          >
            {current?.label}
            <ChevronDown size={13} strokeWidth={2.5} />
          </button>

          {open && (
            <div
              className={
                "absolute right-0 top-full z-10 mt-2 w-40 sm:w-44 overflow-hidden rounded-2xl border shadow-xl " +
                t.card +
                " " +
                t.pillBorder
              }
            >
              {validOptions.map((o, i) => (
                <button
                  key={o.label}
                  onClick={() => {
                    setSelected(i);
                    setOpen(false);
                  }}
                  className={
                    "flex w-full items-center justify-between px-4 py-2.5 text-left text-xs font-medium transition-colors " +
                    (i === selected
                      ? "bg-[#8b7cf6]/15 text-[#8b7cf6]"
                      : t.textMuted + " " + t.surfaceHover)
                  }
                >
                  {o.label}
                  {o.sublabel && (
                    <span className={t.textFaint}>{o.sublabel}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-1">
          <span
            className={
              "text-5xl sm:text-6xl font-extrabold leading-none tracking-tight " +
              t.textPrimary
            }
          >
            {pct}
          </span>
          <span className={"text-2xl font-bold " + t.textFaint}>%</span>
        </div>

        <div className="flex flex-col items-center gap-1.5">
          <span
            className="flex h-14 w-14 sm:h-16 sm:w-16 items-center justify-center rounded-full text-3xl sm:text-4xl"
            style={{
              background:
                "radial-gradient(circle at 35% 30%, #ffe08a, #f6ad3c 60%, #e08a1f 100%)",
              boxShadow: "0 10px 24px -8px rgba(246,173,60,0.55)",
            }}
          >
            {meta.emoji}
          </span>
          <span className={"text-xs sm:text-sm font-semibold " + t.textPrimary}>
            {meta.label}
          </span>
        </div>
      </div>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// Box — Progress of Well-being (real week-over-week / year-over-year chart)
// ---------------------------------------------------------------------------

function CustomTooltip({ active, payload, theme, xKey = "x" }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  const tVal = p.t;
  const lVal = p.l;
  const meta = moodMeta(tVal ?? lVal ?? 0);
  return (
    <div
      className="max-w-[220px] rounded-2xl border px-3.5 py-3 shadow-[0_12px_34px_-10px_rgba(0,0,0,0.35)]"
      style={{
        backgroundColor: theme.tooltipBg,
        borderColor: theme.tooltipBorder,
      }}
    >
      <div className="flex items-start gap-2.5">
        <span
          className={
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-base " +
            theme.surface
          }
        >
          {meta.emoji}
        </span>
        <div>
          <div className={"text-sm font-semibold " + theme.textPrimary}>
            {p[xKey]}
          </div>
          <div
            className={"mt-0.5 text-[11px] leading-relaxed " + theme.textMuted}
          >
            {tVal != null && (
              <div>
                <span style={{ color: "#8b7cf6" }}>●</span> This ({p[xKey]}):{" "}
                <span className={"font-semibold " + theme.textPrimary}>
                  {p.tHasData === false ? "No data" : `${Math.round(tVal)}%`}
                </span>
              </div>
            )}
            {lVal != null && (
              <div>
                <span style={{ color: "#34d9b8" }}>●</span> Prior
                {p.lx ? ` (${p.lx})` : ""}:{" "}
                <span className={"font-semibold " + theme.textPrimary}>
                  {p.lHasData === false ? "No data" : `${Math.round(lVal)}%`}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NEW: builds chart points for an arbitrary user-picked date range, compared
// against the immediately preceding period of equal length (same shape as
// buildWeeklyComparison / buildMonthlyComparison below).
// ---------------------------------------------------------------------------

function buildCustomComparison(daily, startDate, endDate) {
  if (!startDate || !endDate || startDate > endDate) {
    return { points: [], thisRange: undefined, lastRange: undefined };
  }

  const inRange = daily.filter((e) => e.date >= startDate && e.date <= endDate);
  const count = inRange.length;

  const prior = daily.filter((e) => e.date < startDate).slice(-count);

  const points = inRange.map((entry, i) => {
    const priorEntry = prior[i];
    return {
      x: shortDate(entry.date),
      t: entry.organizationAvg,
      l: priorEntry ? priorEntry.organizationAvg : undefined,
      lx: priorEntry ? shortDate(priorEntry.date) : undefined,
    };
  });

  const thisRange =
    inRange.length > 0
      ? inRange.length > 1
        ? `${shortDate(inRange[0].date)} – ${shortDate(inRange[inRange.length - 1].date)}`
        : shortDate(inRange[0].date)
      : undefined;
  const lastRange =
    prior.length > 0
      ? prior.length > 1
        ? `${shortDate(prior[0].date)} – ${shortDate(prior[prior.length - 1].date)}`
        : shortDate(prior[0].date)
      : undefined;

  return { points, thisRange, lastRange };
}

function ProgressOfWellbeing({ weekly, monthly, daily, isSingle = false }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const isMobile = useIsMobile();
  const [range, setRange] = useState("W");
  const [showThis, setShowThis] = useState(true);
  const [showLast, setShowLast] = useState(true);
  const [chartType, setChartType] = useState("bar");
  const [hoverIdx, setHoverIdx] = useState(null);

  // NEW: custom date-range state, defaulted to the last 7 available days
  // the first time real data arrives so the pickers never start empty.
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  useEffect(() => {
    if (!customStart && !customEnd && daily && daily.length > 0) {
      const last = daily[daily.length - 1];
      const first = daily[Math.max(0, daily.length - 7)];
      setCustomEnd(last.date);
      setCustomStart(first.date);
    }
  }, [daily, customStart, customEnd]);

  const customComparison = useMemo(
    () => buildCustomComparison(daily ?? [], customStart, customEnd),
    [daily, customStart, customEnd],
  );

  const activeSet =
    range === "W" ? weekly : range === "M" ? monthly : customComparison;
  const activeData = activeSet.points;
  const thisLabel =
    range === "W"
      ? "Latest Week"
      : range === "M"
        ? "This Year"
        : "Selected Range";
  const lastLabel =
    range === "W"
      ? "Previous Week"
      : range === "M"
        ? "Last Year"
        : "Prior Period";

  const hasAnyLast = activeData.some((p) => p.l != null);
  const hasData = activeData.length > 0;

  const pillBase =
    "flex items-center gap-1.5 sm:gap-2 rounded-full border px-2.5 py-1 sm:px-3 sm:py-1.5 text-[10px] sm:text-xs font-medium transition-all duration-200 hover:-translate-y-0.5";
  const segBase =
    "px-3 sm:px-3.5 py-1.5 text-xs font-semibold transition-colors";

  return (
    <CardShell accent="purple">
      <div className="mb-4 sm:mb-6 flex flex-wrap items-start justify-between gap-3 sm:gap-4">
        <div>
          <h2 className={"text-lg sm:text-xl font-bold " + t.textPrimary}>
            Progress of Well-being
          </h2>
          <p className={"mt-1 text-xs sm:text-sm " + t.textMuted}>
            {isSingle
              ? "Self-reported mood score for this user"
              : "Average self-reported mood score across the org"}
          </p>
          {/* Comparison indicator — always shows exactly which two periods
              the chart is plotting, so it never reads as ambiguous. */}
          {(activeSet.thisRange || activeSet.lastRange) && (
            <p
              className={
                "mt-1.5 text-[11px] sm:text-xs font-medium " + t.textFaint
              }
            >
              Comparing{" "}
              <span style={{ color: "#8b7cf6" }}>
                {thisLabel}
                {activeSet.thisRange ? ` (${activeSet.thisRange})` : ""}
              </span>
              {hasAnyLast && activeSet.lastRange && (
                <>
                  {" "}
                  vs{" "}
                  <span style={{ color: "#34d9b8" }}>
                    {lastLabel} ({activeSet.lastRange})
                  </span>
                </>
              )}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <button
            onClick={() => setShowThis((v) => !v)}
            className={
              pillBase +
              " " +
              (showThis
                ? "border-[#8b7cf6]/50 bg-[#8b7cf6]/15 " + t.textPrimary
                : t.pillBorder + " " + t.surfaceSoft + " " + t.textFaint)
            }
          >
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: showThis ? "#8b7cf6" : "#94a3b8" }}
            />
            {thisLabel}
          </button>
          {hasAnyLast && (
            <button
              onClick={() => setShowLast((v) => !v)}
              className={
                pillBase +
                " " +
                (showLast
                  ? "border-[#34d9b8]/50 bg-[#34d9b8]/15 " + t.textPrimary
                  : t.pillBorder + " " + t.surfaceSoft + " " + t.textFaint)
              }
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: showLast ? "#34d9b8" : "#94a3b8" }}
              />
              {lastLabel}
            </button>
          )}

          <div
            className={
              "flex items-center overflow-hidden rounded-full border " +
              t.pillBorder +
              " " +
              t.surfaceSoft
            }
          >
            <button
              onClick={() => setChartType("bar")}
              title="Bar chart"
              className={
                "flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs font-semibold transition-colors " +
                (chartType === "bar"
                  ? "bg-[#8b7cf6] text-white"
                  : t.textFaint +
                    " hover:" +
                    (isDark ? "text-slate-200" : "text-slate-700"))
              }
            >
              <BarChart3 size={13} strokeWidth={2.5} />
              <span className="hidden xs:inline sm:inline">Bar</span>
            </button>
            <button
              onClick={() => setChartType("area")}
              title="Area chart"
              className={
                "flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs font-semibold transition-colors " +
                (chartType === "area"
                  ? "bg-[#8b7cf6] text-white"
                  : t.textFaint +
                    " hover:" +
                    (isDark ? "text-slate-200" : "text-slate-700"))
              }
            >
              <Activity size={13} strokeWidth={2.5} />
              <span className="hidden xs:inline sm:inline">Area</span>
            </button>
          </div>

          <div
            className={
              "flex overflow-hidden rounded-full border " +
              t.pillBorder +
              " " +
              t.surfaceSoft
            }
          >
            {["W", "M", "C"].map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                title={r === "C" ? "Custom range" : undefined}
                className={
                  segBase +
                  " " +
                  (range === r
                    ? "bg-[#8b7cf6] text-white"
                    : t.textFaint +
                      " hover:" +
                      (isDark ? "text-slate-200" : "text-slate-700"))
                }
              >
                {r}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* NEW: date pickers, shown only in Custom mode */}
      {range === "C" && (
        <div className="mb-4 sm:mb-5 flex flex-wrap items-center gap-2 sm:gap-3">
          <label
            className={
              "flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] sm:text-xs font-medium " +
              t.pillBorder +
              " " +
              t.surfaceSoft +
              " " +
              t.textMuted
            }
          >
            From
            <input
              type="date"
              value={customStart}
              max={customEnd || undefined}
              onChange={(e) => setCustomStart(e.target.value)}
              className={
                "bg-transparent outline-none " +
                t.textPrimary +
                " [color-scheme:" +
                (isDark ? "dark" : "light") +
                "]"
              }
            />
          </label>
          <label
            className={
              "flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] sm:text-xs font-medium " +
              t.pillBorder +
              " " +
              t.surfaceSoft +
              " " +
              t.textMuted
            }
          >
            To
            <input
              type="date"
              value={customEnd}
              min={customStart || undefined}
              onChange={(e) => setCustomEnd(e.target.value)}
              className={
                "bg-transparent outline-none " +
                t.textPrimary +
                " [color-scheme:" +
                (isDark ? "dark" : "light") +
                "]"
              }
            />
          </label>
        </div>
      )}

      {!hasData ? (
        <div
          className={
            "flex h-56 sm:h-72 w-full items-center justify-center rounded-2xl text-sm " +
            t.surfaceSoft +
            " " +
            t.textMuted
          }
        >
          Not enough data yet to plot this range.
        </div>
      ) : (
        <div className="h-56 sm:h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === "bar" ? (
              <BarChart
                data={activeData}
                margin={{
                  top: 10,
                  right: isMobile ? 0 : 10,
                  left: isMobile ? -28 : -10,
                  bottom: 0,
                }}
                barGap={isMobile ? 3 : 6}
                onMouseMove={(s) =>
                  setHoverIdx(
                    s && s.isTooltipActive ? s.activeTooltipIndex : null,
                  )
                }
                onMouseLeave={() => setHoverIdx(null)}
              >
                <defs>
                  <linearGradient id="thisBarGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#a78bfa" stopOpacity={1} />
                    <stop
                      offset="100%"
                      stopColor="#8b7cf6"
                      stopOpacity={0.25}
                    />
                  </linearGradient>
                  <linearGradient
                    id="thisBarGradActive"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor="#c4b5fd" stopOpacity={1} />
                    <stop
                      offset="100%"
                      stopColor="#8b7cf6"
                      stopOpacity={0.55}
                    />
                  </linearGradient>
                  <linearGradient id="lastBarGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#5eead4" stopOpacity={0.9} />
                    <stop
                      offset="100%"
                      stopColor="#34d9b8"
                      stopOpacity={0.15}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="4 6"
                  stroke={t.gridStroke}
                  vertical={false}
                />
                <XAxis
                  dataKey="x"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: t.axisTick, fontSize: isMobile ? 10 : 12 }}
                  interval={isMobile ? "preserveStartEnd" : 0}
                  dy={8}
                />
                <YAxis
                  domain={[0, 100]}
                  ticks={isMobile ? [0, 50, 100] : [0, 20, 40, 60, 80, 100]}
                  axisLine={false}
                  tickLine={false}
                  width={isMobile ? 28 : 36}
                  tick={{ fill: t.axisTick, fontSize: isMobile ? 10 : 11 }}
                />
                <Tooltip
                  content={<CustomTooltip theme={t} />}
                  cursor={{
                    fill: isDark
                      ? "rgba(255,255,255,0.04)"
                      : "rgba(15,23,42,0.04)",
                  }}
                />
                {showLast && hasAnyLast && (
                  <Bar
                    dataKey="l"
                    fill="url(#lastBarGrad)"
                    radius={[8, 8, 0, 0]}
                    maxBarSize={isMobile ? 16 : 26}
                    animationDuration={500}
                  />
                )}
                {showThis && (
                  <Bar
                    dataKey="t"
                    radius={[8, 8, 0, 0]}
                    maxBarSize={isMobile ? 16 : 26}
                    animationDuration={500}
                  >
                    {activeData.map((_, i) => (
                      <Cell
                        key={i}
                        fill={
                          i === hoverIdx
                            ? "url(#thisBarGradActive)"
                            : "url(#thisBarGrad)"
                        }
                      />
                    ))}
                  </Bar>
                )}
              </BarChart>
            ) : (
              <AreaChart
                data={activeData}
                margin={{
                  top: 10,
                  right: isMobile ? 0 : 10,
                  left: isMobile ? -28 : -10,
                  bottom: 0,
                }}
              >
                <defs>
                  <linearGradient id="thisGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8b7cf6" stopOpacity={0.55} />
                    <stop
                      offset="100%"
                      stopColor="#8b7cf6"
                      stopOpacity={0.02}
                    />
                  </linearGradient>
                  <linearGradient id="lastGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#34d9b8" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#34d9b8" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="4 6"
                  stroke={t.gridStroke}
                  vertical={false}
                />
                <XAxis
                  dataKey="x"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: t.axisTick, fontSize: isMobile ? 10 : 12 }}
                  interval={isMobile ? "preserveStartEnd" : 0}
                  dy={8}
                />
                <YAxis
                  domain={[0, 100]}
                  ticks={isMobile ? [0, 50, 100] : [0, 20, 40, 60, 80, 100]}
                  axisLine={false}
                  tickLine={false}
                  width={isMobile ? 28 : 36}
                  tick={{ fill: t.axisTick, fontSize: isMobile ? 10 : 11 }}
                />
                <Tooltip
                  content={<CustomTooltip theme={t} />}
                  cursor={{
                    stroke: isDark
                      ? "rgba(255,255,255,0.15)"
                      : "rgba(15,23,42,0.15)",
                  }}
                />
                {showLast && hasAnyLast && (
                  <Area
                    type="monotone"
                    dataKey="l"
                    stroke="#34d9b8"
                    strokeWidth={2}
                    fill="url(#lastGrad)"
                    animationDuration={600}
                    connectNulls
                  />
                )}
                {showThis && (
                  <Area
                    type="monotone"
                    dataKey="t"
                    stroke="#8b7cf6"
                    strokeWidth={2.5}
                    fill="url(#thisGrad)"
                    animationDuration={600}
                    connectNulls
                    dot={{
                      r: isMobile ? 2 : 3,
                      fill: "#8b7cf6",
                      strokeWidth: 0,
                    }}
                    activeDot={{
                      r: isMobile ? 4 : 5,
                      fill: "#fff",
                      stroke: "#8b7cf6",
                      strokeWidth: 2,
                    }}
                  />
                )}
              </AreaChart>
            )}
          </ResponsiveContainer>
        </div>
      )}

      {hasData && (
        <div
          className={
            "mt-4 flex items-start sm:items-center gap-2 rounded-2xl bg-gradient-to-r from-[#8b7cf6]/10 to-transparent px-3.5 sm:px-4 py-2.5 sm:py-3 text-[11px] sm:text-xs " +
            t.textMuted
          }
        >
          <Sparkles
            size={14}
            className="mt-0.5 sm:mt-0 shrink-0 text-[#8b7cf6]"
          />
          {(() => {
            const best = activeData.reduce(
              (b, p) => (p.t != null && (b == null || p.t > b.t) ? p : b),
              null,
            );
            return best ? (
              <span>
                Well-being peaked at{" "}
                <span className={"font-semibold " + t.textPrimary}>
                  {Math.round(best.t)}%
                </span>{" "}
                around{" "}
                <span className={"font-semibold " + t.textPrimary}>
                  {best.x}
                </span>
                .
              </span>
            ) : (
              "Not enough data yet to highlight a peak."
            );
          })()}
        </div>
      )}
    </CardShell>
  );
}

// APIS
const API_BASE = `${serverUrl}api/v1/users`;
const Mail_BASE_URl = "https://healo-api.infiheal.com/api";

const moodFetch = (code, url, opts) =>
  DEMO_DATA[code]?.mood ? demoMoodFetch(url) : fetch(url, opts);

const MOOD_DETAILS_URL = (code, userId) =>
  userId
    ? `${API_BASE}/moodDetails/${code}/user/${userId}`
    : `${API_BASE}/moodDetails/${code}`;
const MOOD_USERS_URL = (code) => `${API_BASE}/moodUsers/${code}`;

const MOOD_BY_CODE_URL = (code, userId) =>
  userId
    ? `${serverUrl}api/v1/users/moodPatterns/${code}/user/${userId}`
    : `${serverUrl}api/v1/users/moodPatterns/${code}`;

const THEME_BY_CODE_URL = (code, emotion, userId) =>
  userId
    ? `${serverUrl}api/v1/users/moodThemeDetails/${code}/user/${userId}/${encodeURIComponent(emotion)}`
    : `${serverUrl}api/v1/users/moodThemeDetails/${code}/${encodeURIComponent(emotion)}`;

const USER_ACTIVITY_URL = (code, page = 1, limit = 10) =>
  `${serverUrl}api/v1/users/userActivity/${code}?page=${page}&limit=${limit}`;

const USER_ENGAGEMENT_URL = (code, weeks = 12) =>
  `${serverUrl}api/v1/users/userEngagement/${code}?weeks=${weeks}`;

// emotionsAvg key -> display label, icon, brand tint, and the exact string
// the theme-by-emotion endpoint expects.
const EMOTION_META = {
  sadness: {
    label: "Sadness",
    apiEmotion: "Sadness",
    icon: CloudRain,
    tint: BRAND.purple,
  },
  loneliness: {
    label: "Loneliness",
    apiEmotion: "Loneliness",
    icon: User,
    tint: BRAND.purple,
  },
  numbness: {
    label: "Numbness",
    apiEmotion: "Numbness",
    icon: CircleSlash,
    tint: BRAND.silver,
  },
  shame: {
    label: "Shame",
    apiEmotion: "Shame",
    icon: EyeOff,
    tint: BRAND.bronze,
  },
  guilt: {
    label: "Guilt",
    apiEmotion: "Guilt",
    icon: Scale,
    tint: BRAND.silver,
  },
  anger: { label: "Anger", apiEmotion: "Anger", icon: Flame, tint: "#f0653c" },
  anxiety: {
    label: "Anxiety",
    apiEmotion: "Fear_Anxiety",
    icon: Zap,
    tint: BRAND.gold,
  },
  disappointment: {
    label: "Disappointment",
    apiEmotion: "Disappointment/Sadness",
    icon: FrownIcon,
    tint: BRAND.bronze,
  },
  hurt: {
    label: "Hurt",
    apiEmotion: "Hurt(By Others)",
    icon: HeartCrack,
    tint: BRAND.pink,
  },
  grief: {
    label: "Grief",
    apiEmotion: "Grief_Loss",
    icon: Cloud,
    tint: "#5b6b93",
  },
  envy: { label: "Envy", apiEmotion: "Envy", icon: Eye, tint: "#6fae4f" },
  joy: { label: "Joy", apiEmotion: "Joy", icon: Smile, tint: BRAND.teal },
};

// Matches THEME_LABEL_TO_KEY on the backend, reversed, for display.
const THEME_KEY_TO_LABEL = {
  relationShipRomance: "Relationships/Romance",
  family: "Family (Parents, siblings)",
  raisingChildren: "Raising Children/Parenting",
  pregnancy: "Pregnancy",
  work: "Work/Academics",
  healthBody: "Health/Body",
  selfWorthIdentity: "Self-worth/Identity",
  financesCareer: "Finances/Career",
  geriatricConcerns: "Geriatric Concerns",
  sexuality: "Sexuality",
  socialLife: "Social Life",
  hobbiesSelfExpression: "Hobbies/Self Expression",
  spirituality: "Spirituality",
};

const DONUT_COLORS = [
  BRAND.purple,
  BRAND.teal,
  BRAND.pink,
  BRAND.gold,
  BRAND.bronze,
  "#6fae4f",
  "#5b6b93",
  BRAND.silver,
];

function PatternRow({ emotionKey, meta, score, onOpen }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const Icon = meta.icon;

  return (
    <div
      className={
        "flex items-center justify-between gap-3 rounded-2xl px-3.5 sm:px-4 py-3 sm:py-3.5 transition-colors " +
        t.surfaceSoft +
        " " +
        t.surfaceHover
      }
    >
      <div className="flex min-w-0 items-center gap-3">
        <span
          className="flex h-10 w-10 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: `${meta.tint}22`, color: meta.tint }}
        >
          <Icon size={19} strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <div
            className={"text-sm sm:text-base font-semibold " + t.textPrimary}
          >
            {meta.label}
          </div>
          <div className={"text-xs sm:text-sm " + t.textFaint}>
            Avg. Score: {Math.round(score)}
          </div>
        </div>
      </div>

      <button
        onClick={() => onOpen(emotionKey)}
        className="shrink-0 rounded-full px-4 sm:px-5 py-2 text-xs sm:text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0"
        style={{
          background: BRAND.gradient,
          boxShadow: "0 10px 24px -10px rgba(239,93,168,0.55)",
        }}
      >
        View Insights
      </button>
    </div>
  );
}

function PatternsRowSkeleton() {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  return (
    <div
      className={
        "flex items-center gap-3 rounded-2xl px-4 py-3.5 animate-pulse " +
        t.surfaceSoft
      }
    >
      <div className={"h-10 w-10 sm:h-11 sm:w-11 rounded-full " + t.surface} />
      <div className="flex-1 space-y-2">
        <div className={"h-3.5 w-24 rounded " + t.surface} />
        <div className={"h-3 w-32 rounded " + t.surface} />
      </div>
      <div className={"h-8 w-24 rounded-full " + t.surface} />
    </div>
  );
}

function PatternsWeNoticed({ code, userId }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const [emotionsAvg, setEmotionsAvg] = useState(null);
  const [totalUsers, setTotalUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeEmotionKey, setActiveEmotionKey] = useState(null);

  useEffect(() => {
    if (!code) return;
    const load = async () => {
      try {
        setLoading(true);
        const res = await moodFetch(code, MOOD_BY_CODE_URL(code, userId));
        const json = await res.json();
        setEmotionsAvg(json?.data?.emotionsAvg ?? null);
        setTotalUsers(json?.data?.totalUsers ?? null);
        setError(false);
      } catch (err) {
        console.log(err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [code, userId]);

  const topEmotions = useMemo(() => {
    if (!emotionsAvg) return [];
    return Object.entries(emotionsAvg)
      .filter(([key, value]) => EMOTION_META[key] && (value ?? 0) > 0)
      .sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0))
      .slice(0, 5)
      .map(([key, value]) => ({ key, value, meta: EMOTION_META[key] }));
  }, [emotionsAvg]);

  return (
    <>
      <CardShell accent="pink">
        <div className="mb-4 sm:mb-5 flex items-start justify-between gap-3">
          <div>
            <h2 className={"text-lg sm:text-xl font-bold " + t.textPrimary}>
              Patterns We've Noticed
            </h2>

            <p className={"mt-1 text-xs sm:text-sm " + t.textMuted}>
              {userId ? (
                "The emotions showing up most for this user"
              ) : (
                <>
                  The emotions showing up most across the org
                  {totalUsers ? ` · ${totalUsers} team members` : ""}
                </>
              )}
            </p>
          </div>
        </div>

        {error ? (
          <div
            className={
              "rounded-2xl px-4 py-6 text-center text-sm " +
              t.surfaceSoft +
              " " +
              t.textMuted
            }
          >
            Couldn't load patterns right now — please try again shortly.
          </div>
        ) : loading ? (
          <div className="space-y-2.5">
            <PatternsRowSkeleton />
            <PatternsRowSkeleton />
            <PatternsRowSkeleton />
          </div>
        ) : topEmotions.length === 0 ? (
          <div
            className={
              "rounded-2xl px-4 py-6 text-center text-sm " +
              t.surfaceSoft +
              " " +
              t.textMuted
            }
          >
            No strong emotional patterns detected yet.
          </div>
        ) : (
          <div className="space-y-2.5">
            {topEmotions.map((e) => (
              <PatternRow
                key={e.key}
                emotionKey={e.key}
                meta={e.meta}
                score={e.value}
                onOpen={setActiveEmotionKey}
              />
            ))}
          </div>
        )}

        <p className={"mt-4 text-[11px] leading-relaxed " + t.textFaint}>
          Disclaimer: These insights are derived only from anonymized org-wide
          conversations and are not a substitute for a professional diagnosis.
        </p>
      </CardShell>

      {activeEmotionKey && (
        <EmotionInsightModal
          code={code}
          userId={userId}
          emotionKey={activeEmotionKey}
          score={emotionsAvg?.[activeEmotionKey]}
          onClose={() => setActiveEmotionKey(null)}
        />
      )}
    </>
  );
}

function EmotionInsightModal({ code, userId, emotionKey, score, onClose }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const meta = EMOTION_META[emotionKey];
  const Icon = meta.icon;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [openThemeKey, setOpenThemeKey] = useState(null);

  useEffect(() => {
    if (!code || !emotionKey) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const res = await moodFetch(code, THEME_BY_CODE_URL(code, meta.apiEmotion, userId));
        const json = await res.json();
        if (cancelled) return;
        setData(json?.data ?? null);
        setOpenThemeKey(json?.data?.orderedThemes?.[0]?.key ?? null);
        setError(false);
      } catch (err) {
        if (!cancelled) {
          console.log(err);
          setError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [code, userId, emotionKey, meta.apiEmotion]);

  const severity = severityMeta(score);
  const orderedThemes = data?.orderedThemes ?? [];
  const totalCount = data?.totalCount ?? 0;

  const donutData = orderedThemes.map((theme, i) => ({
    key: theme.key,
    name: THEME_KEY_TO_LABEL[theme.key] ?? theme.key,
    value: theme.count,
    pct: totalCount ? Math.round((theme.count / totalCount) * 100) : 0,
    color: DONUT_COLORS[i % DONUT_COLORS.length],
  }));

  return (
    <div
      className={
        "fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm " +
        t.overlay
      }
      onClick={onClose}
    >
      <div
        className={
          "flex max-h-[88vh] w-full max-w-lg flex-col overflow-hidden rounded-3xl border " +
          t.card +
          " " +
          t.cardShadow
        }
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={
            "flex items-center gap-3 border-b px-4 sm:px-5 py-4 " + t.pillBorder
          }
        >
          <button
            onClick={onClose}
            className={
              "flex h-8 w-8 items-center justify-center rounded-full transition-colors " +
              t.surfaceSoft +
              " " +
              t.surfaceHover +
              " " +
              t.textMuted
            }
            aria-label="Back"
          >
            <ChevronLeft size={17} strokeWidth={2.5} />
          </button>
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
            style={{ backgroundColor: `${meta.tint}22`, color: meta.tint }}
          >
            <Icon size={18} strokeWidth={2} />
          </span>
          <h3
            className={"flex-1 text-base sm:text-lg font-bold " + t.textPrimary}
          >
            {meta.label}
          </h3>
          <button
            onClick={onClose}
            className={
              "flex h-8 w-8 items-center justify-center rounded-full transition-colors " +
              t.surfaceSoft +
              " " +
              t.surfaceHover +
              " " +
              t.textMuted
            }
            aria-label="Close"
          >
            <X size={17} strokeWidth={2.5} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 sm:py-5">
          {/* Average score box */}
          <div
            className={
              "flex items-center justify-between rounded-2xl px-4 sm:px-5 py-4 " +
              t.surfaceSoft
            }
          >
            <div>
              <div className={"text-xs sm:text-sm font-medium " + t.textMuted}>
                Average Emotion Score
              </div>
              <div className="mt-1 flex items-baseline gap-1">
                <span
                  className={
                    "text-3xl sm:text-4xl font-extrabold leading-none " +
                    t.textPrimary
                  }
                >
                  {score != null ? Math.round(score) : "—"}
                </span>
                <span className={"text-sm font-semibold " + t.textFaint}>
                  /100
                </span>
              </div>
            </div>
            <div className="flex flex-col items-center gap-1.5">
              <span
                className="flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full text-2xl sm:text-3xl"
                style={{
                  background: `radial-gradient(circle at 35% 30%, ${severity.tint}cc, ${severity.tint} 70%)`,
                  boxShadow: `0 10px 22px -10px ${severity.tint}99`,
                }}
              >
                {severity.emoji}
              </span>
              <span className={"text-xs font-semibold " + t.textPrimary}>
                {severity.label}
              </span>
            </div>
          </div>

          {/* Themes */}
          <h4
            className={
              "mb-3 mt-5 text-sm sm:text-base font-bold " + t.textPrimary
            }
          >
            Common Themes Linked to {meta.label}
          </h4>

          {loading ? (
            <div
              className={
                "flex h-40 items-center justify-center rounded-2xl text-sm animate-pulse " +
                t.surfaceSoft +
                " " +
                t.textMuted
              }
            >
              Loading insights…
            </div>
          ) : error ? (
            <div
              className={
                "flex h-40 items-center justify-center rounded-2xl text-sm " +
                t.surfaceSoft +
                " " +
                t.textMuted
              }
            >
              Couldn't load themes right now.
            </div>
          ) : donutData.length === 0 ? (
            <div
              className={
                "flex h-40 items-center justify-center rounded-2xl text-sm " +
                t.surfaceSoft +
                " " +
                t.textMuted
              }
            >
              No linked themes found yet.
            </div>
          ) : (
            <div
              className={
                "flex items-center gap-4 sm:gap-6 rounded-2xl px-4 sm:px-5 py-4 sm:py-5 " +
                t.surfaceSoft
              }
            >
              <div className="h-28 w-28 sm:h-32 sm:w-32 shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={donutData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius="62%"
                      outerRadius="100%"
                      paddingAngle={donutData.length > 1 ? 3 : 0}
                      stroke="none"
                    >
                      {donutData.map((d) => (
                        <Cell key={d.key} fill={d.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="min-w-0 flex-1 space-y-2">
                {donutData.map((d) => (
                  <div
                    key={d.key}
                    className="flex items-center justify-between gap-2 text-xs sm:text-sm"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: d.color }}
                      />
                      <span className={"truncate font-medium " + t.textPrimary}>
                        {d.name}
                      </span>
                    </span>
                    <span className={"shrink-0 font-semibold " + t.textFaint}>
                      {d.pct}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Deeper patterns accordion */}
          {!loading && !error && orderedThemes.length > 0 && (
            <>
              <div className="mb-3 mt-5 flex items-center gap-2">
                <h4
                  className={"text-sm sm:text-base font-bold " + t.textPrimary}
                >
                  Deeper Patterns we Noticed
                </h4>
                <span className="rounded-full border border-emerald-400/30 bg-emerald-400/15 px-2 py-0.5 text-[10px] font-semibold text-emerald-400">
                  New
                </span>
              </div>

              <div className="space-y-2">
                {orderedThemes.map((theme, i) => {
                  const isOpen = openThemeKey === theme.key;
                  const color = DONUT_COLORS[i % DONUT_COLORS.length];
                  return (
                    <div
                      key={theme.key}
                      className={"overflow-hidden rounded-2xl " + t.surfaceSoft}
                    >
                      <button
                        onClick={() =>
                          setOpenThemeKey(isOpen ? null : theme.key)
                        }
                        className="flex w-full items-center justify-between px-4 py-3 text-left"
                      >
                        <span className="flex items-center gap-2 text-xs sm:text-sm font-semibold">
                          <span
                            className="h-2 w-2 shrink-0 rounded-full"
                            style={{ backgroundColor: color }}
                          />
                          <span className={t.textPrimary}>
                            {THEME_KEY_TO_LABEL[theme.key] ?? theme.key}
                          </span>
                        </span>
                        {isOpen ? (
                          <ChevronUp size={15} className={t.textFaint} />
                        ) : (
                          <ChevronDown size={15} className={t.textFaint} />
                        )}
                      </button>
                      {isOpen && (
                        <div className="px-4 pb-3.5">
                          {theme.subThemes && theme.subThemes.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5">
                              {theme.subThemes.map((st) => (
                                <span
                                  key={st}
                                  className={
                                    "rounded-full border px-2.5 py-1 text-[11px] font-medium " +
                                    t.pillBorder +
                                    " " +
                                    t.textMuted
                                  }
                                >
                                  {st}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className={"text-xs sm:text-sm " + t.textFaint}>
                              No sub-themes found yet.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <p
            className={
              "mt-5 text-center text-[11px] leading-relaxed " + t.textFaint
            }
          >
            Disclaimer: This insight is derived only from our discussions and is
            not a substitute for a professional diagnosis.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Data helpers — turn the raw API payload into chart/card-ready shapes.
// Each comparison also returns a human-readable "thisRange"/"lastRange"
// label so the UI can always spell out exactly which two periods are
// being plotted against each other (e.g. "Jul 8 – Jul 14" vs "Jul 1 – Jul 7").
// ---------------------------------------------------------------------------

function buildWeeklyComparison(daily) {
  const last14 = daily.slice(-14);
  const recent = last14.slice(-7);
  const prior = last14.slice(0, last14.length - recent.length);

  const points = recent.map((entry, i) => {
    const priorEntry = prior[i];
    return {
      x: shortDate(entry.date),
      t: entry.organizationAvg,
      tHasData: entry.hasData !== false,
      l: priorEntry ? priorEntry.organizationAvg : undefined,
      lHasData: priorEntry ? priorEntry.hasData !== false : undefined,
      lx: priorEntry ? shortDate(priorEntry.date) : undefined,
    };
  });

  const thisRange =
    recent.length > 0
      ? recent.length > 1
        ? `${shortDate(recent[0].date)} – ${shortDate(recent[recent.length - 1].date)}`
        : shortDate(recent[0].date)
      : undefined;
  const lastRange =
    prior.length > 0
      ? prior.length > 1
        ? `${shortDate(prior[0].date)} – ${shortDate(prior[prior.length - 1].date)}`
        : shortDate(prior[0].date)
      : undefined;

  return { points, thisRange, lastRange };
}

function buildMonthlyComparison(monthly) {
  if (monthly.length === 0)
    return { points: [], thisRange: undefined, lastRange: undefined };
  const monthMap = new Map(monthly.map((m) => [m.month, m]));
  const [latestYear] = monthly[monthly.length - 1].month.split("-");
  const currentYearMonths = monthly.filter((m) =>
    m.month.startsWith(latestYear),
  );
  const priorYear = String(Number(latestYear) - 1);
  const hasPriorYearData = currentYearMonths.some((entry) => {
    const [, m] = entry.month.split("-");
    return monthMap.has(`${priorYear}-${m}`);
  });

  const points = currentYearMonths.map((entry) => {
    const [, m] = entry.month.split("-");
    const priorKey = `${priorYear}-${m}`;
    const priorEntry = monthMap.get(priorKey);
    return {
      x: shortMonth(entry.month),
      t: entry.organizationAvg,
      l: priorEntry ? priorEntry.organizationAvg : undefined,
      lx: priorEntry
        ? `${shortMonth(priorEntry.month)} ${priorYear}`
        : undefined,
    };
  });

  return {
    points,
    thisRange: latestYear,
    lastRange: hasPriorYearData ? priorYear : undefined,
  };
}


function ViewModeBar({
  viewMode,
  onModeChange,
  members,
  membersLoading,
  selectedUserId,
  onSelectUser,
}) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];

  const segBase =
    "px-3.5 sm:px-4 py-1.5 text-xs font-semibold transition-colors";
  const modes = [
    { key: "overall", label: "Overall" },
    { key: "single", label: "Individual" },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2.5 sm:gap-3 px-4 sm:px-6 md:px-10 pt-6 md:pt-10 -mb-1">
      <div
        className={
          "flex overflow-hidden rounded-full border " +
          t.pillBorder +
          " " +
          t.surfaceSoft
        }
      >
        {modes.map((m) => (
          <button
            key={m.key}
            onClick={() => onModeChange(m.key)}
            className={
              segBase +
              " " +
              (viewMode === m.key
                ? "bg-[#8b7cf6] text-white"
                : t.textFaint +
                  " hover:" +
                  (isDark ? "text-slate-200" : "text-slate-700"))
            }
          >
            {m.label}
          </button>
        ))}
      </div>

      {viewMode === "single" && (
        <label
          className={
            "flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] sm:text-xs font-medium " +
            t.pillBorder +
            " " +
            t.surfaceSoft +
            " " +
            t.textMuted
          }
        >
          User
          <select
            value={selectedUserId}
            onChange={(e) => onSelectUser(e.target.value)}
            disabled={membersLoading || members.length === 0}
            className={
              "bg-transparent outline-none max-w-[180px] " +
              t.textPrimary +
              " [color-scheme:" +
              (isDark ? "dark" : "light") +
              "]"
            }
          >
            {membersLoading && <option value="">Loading…</option>}
            {!membersLoading && members.length === 0 && (
              <option value="">No users found</option>
            )}
            {members.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m?.displayName}
              </option>
            ))}
          </select>
        </label>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// User Activity — one row per user: id, onboarding date, last used,
// message count, send-reminder button.
//   REAL:  id + onboardingDate (+ lastNotificationAt) from /userActivity/:code
//   DUMMY: last chat time + message count (conversation_history API not built
//          yet) — replace getDummyConversationStats() when it exists.
//   FAKE:  reminder mail — simulated with loader + toast.
// ---------------------------------------------------------------------------

function formatDuration(seconds) {
  if (seconds == null) return "—";
  if (seconds <= 0) return "0s";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

function formatFullDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function timeAgo(iso) {
  if (!iso) return "Never";
  const diff = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diff)) return "Never";
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return formatFullDate(iso);
}

function pickLatest(a, b) {
  if (!a) return b;
  if (!b) return a;
  return new Date(a) > new Date(b) ? a : b;
}

function Toast({ toast, onClose }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(onClose, 3200);
    return () => clearTimeout(id);
  }, [toast, onClose]);
  if (!toast) return null;
  const isErr = toast.type === "error";
  return (
    <div
      className={
        "fixed bottom-5 right-5 z-[60] flex max-w-[calc(100vw-2.5rem)] items-center gap-2.5 rounded-2xl border px-4 py-3 text-sm font-medium shadow-2xl backdrop-blur " +
        t.card +
        " " +
        t.textPrimary
      }
    >
      {isErr ? (
        <AlertCircle size={17} style={{ color: BRAND.pink }} />
      ) : (
        <CheckCircle2 size={17} style={{ color: BRAND.teal }} />
      )}
      {toast.message}
    </div>
  );
}

function UserActivityRowSkeleton() {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  return <div className={"h-16 animate-pulse rounded-2xl " + t.surfaceSoft} />;
}

function UserActivityRow({ row, gridCols, onRemind, sending, sent,showRemind }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const stats = row.stats;

  const mobileLabel =
    "md:hidden text-[10px] uppercase tracking-wide " + t.textFaint;

  return (
    <div
      className={
        "grid grid-cols-2 items-center gap-x-3 gap-y-3 rounded-2xl px-3.5 sm:px-4 py-3 sm:py-3.5 transition-colors " +
        gridCols +
        " " +
        t.surfaceSoft +
        " " +
        t.surfaceHover
      }
    >
      <div className="min-w-0">
        <div className={mobileLabel}>ID</div>
        <div
          title={String(row.userId)}
          className={"truncate text-sm font-semibold " + t.textPrimary}
        >
          {row?.alias ?? row?.email ?? row?.userId}
        </div>
        {row.label && (
          <div className={"truncate text-xs " + t.textFaint}>
            {row?.email ?? row?.userId}
            {row.type === "anonymous" ? " · anon" : ""}
          </div>
        )}
      </div>

      <div>
        <div className={mobileLabel}>Onboarding Date</div>
        <div className={"text-sm " + t.textMuted}>
          {formatFullDate(row.onboardingDate)}
        </div>
      </div>

      <div>
        <div className={mobileLabel}>Last Used</div>
        <div className={"text-sm " + t.textMuted}>
          {timeAgo(stats?.lastChatAt)}
        </div>
      </div>

      <div>
        <div className={mobileLabel}>Messages</div>
        <div className={"text-sm font-semibold " + t.textPrimary}>
          {stats?.messageCount ?? "—"}
        </div>
      </div>

      <div>
        <div className={mobileLabel}>Avg Chat</div>
        <div
          title={
            stats
              ? `${stats.conversationsCounted} of ${stats.conversationCount} conversations counted`
              : undefined
          }
          className={"text-sm font-semibold " + t.textPrimary}
        >
          {stats ? formatDuration(stats.avgChatDurationSeconds) : "—"}
        </div>
      </div>

      <div>
        <div className={mobileLabel}>Tests Taken</div>
        <div className={"text-sm font-semibold " + t.textPrimary}>
          {stats?.testsTaken ?? "—"}
        </div>
      </div>

       <div className="col-span-2 md:col-span-1 md:justify-self-end">
        {showRemind && (
          <button
            onClick={() => onRemind(row)}
            disabled={sending}
            className="flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
            style={{ background: BRAND.gradient }}
          >
            {sent ? <CheckCircle2 size={13} /> : <Send size={13} />}
            {sent ? "Send Again" : "Remind"}
          </button>
        )}
      </div>
    </div>
  );
}
function PaginationBar({
  page,
  totalPages,
  totalUsers,
  limit,
  loading,
  onChange,
}) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];

  const from = totalUsers === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, totalUsers);

  // compact window of page numbers around the current page
  const pages = [];
  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
  const end = Math.min(totalPages, start + 4);
  for (let p = start; p <= end; p++) pages.push(p);

  const btn =
    "flex h-8 min-w-8 items-center justify-center rounded-full border px-2 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 " +
    t.pillBorder +
    " " +
    t.surfaceSoft +
    " " +
    t.textMuted +
    " " +
    t.surfaceHover;

  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
      <span className={"text-xs " + t.textFaint}>
        Showing {from}–{to} of {totalUsers}
      </span>
      <div className="flex items-center gap-1.5">
        <button
          className={btn}
          disabled={page <= 1 || loading}
          onClick={() => onChange(page - 1)}
          aria-label="Previous page"
        >
          <ChevronLeft size={15} strokeWidth={2.5} />
        </button>
        {pages.map((p) => (
          <button
            key={p}
            disabled={loading}
            onClick={() => onChange(p)}
            className={
              "flex h-8 min-w-8 items-center justify-center rounded-full px-2 text-xs font-semibold transition-colors " +
              (p === page
                ? "bg-[#8b7cf6] text-white"
                : "border " +
                  t.pillBorder +
                  " " +
                  t.surfaceSoft +
                  " " +
                  t.textMuted +
                  " " +
                  t.surfaceHover)
            }
          >
            {p}
          </button>
        ))}
        <button
          className={btn}
          disabled={page >= totalPages || loading}
          onClick={() => onChange(page + 1)}
          aria-label="Next page"
        >
          <ChevronRight size={15} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}
function ReminderEmailModal({
  row,
  recipients = [],
  sending,
  onCancel,
  onSend,
}) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];

  const isBulk = !!row.bulkCount;
  const name = isBulk ? "there" : row.label || "there";

  const [subject, setSubject] = useState("We miss you on Infiheal 💗");
  const [body, setBody] = useState(
    `Hi {{alias}},\n\nWe noticed you haven't checked in for a while. Taking a few minutes for yourself can make a real difference.\n\nWhenever you're ready, we're here for you — private and judgment-free.\n\nWarm regards,\nTeam Infiheal`,
  );
  const [touched, setTouched] = useState(false);

  const noRecipients = recipients.length === 0;
  const invalid = noRecipients || !subject.trim() || !body.trim();

  const fieldBase =
    "w-full rounded-xl border px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-[#8b7cf6]/60 " +
    t.pillBorder +
    " " +
    t.surfaceSoft +
    " " +
    t.textPrimary;
  const labelCls = "mb-1.5 block text-xs font-semibold " + t.textMuted;
  const errCls = "border-[#ef5da8]/60";

  const handleSend = () => {
    setTouched(true);
    if (invalid || sending) return;
    onSend(row, { subject: subject.trim(), body: body.trim() });
  };

  return (
    <div
      className={
        "fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur-sm " +
        t.overlay
      }
      onClick={() => !sending && onCancel()}
    >
      <div
        className={
          "flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-3xl border " +
          t.card +
          " " +
          t.cardShadow
        }
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className={
            "flex items-center justify-between border-b px-4 sm:px-5 py-4 " +
            t.pillBorder
          }
        >
          <h3 className={"text-base sm:text-lg font-bold " + t.textPrimary}>
            Send Reminder
          </h3>
          <button
            onClick={onCancel}
            disabled={sending}
            aria-label="Close"
            className={
              "flex h-8 w-8 items-center justify-center rounded-full transition-colors disabled:opacity-50 " +
              t.surfaceSoft +
              " " +
              t.surfaceHover +
              " " +
              t.textMuted
            }
          >
            <X size={17} strokeWidth={2.5} />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 sm:px-5 py-4 sm:py-5">
          <RecipientsField
            recipients={recipients}
            labelCls={labelCls}
            fieldBase={fieldBase}
            error={touched && noRecipients}
            errCls={errCls}
          />
          <div>
            <label className={labelCls}>Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={sending}
              className={
                fieldBase + " " + (touched && !subject.trim() ? errCls : "")
              }
            />
          </div>
          <div>
            <label className={labelCls}>Message</label>
            <textarea
              rows={9}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={sending}
              className={
                fieldBase +
                " resize-none leading-relaxed " +
                (touched && !body.trim() ? errCls : "")
              }
            />
          </div>
          {touched && invalid && (
            <p className="text-xs font-medium" style={{ color: BRAND.pink }}>
              Please fill in all the fields before sending.
            </p>
          )}
        </div>

        <div
          className={
            "flex items-center justify-end gap-2.5 border-t px-4 sm:px-5 py-3.5 " +
            t.pillBorder
          }
        >
          <button
            onClick={onCancel}
            disabled={sending}
            className={
              "rounded-full border px-5 py-2 text-xs sm:text-sm font-semibold transition-colors disabled:opacity-50 " +
              t.pillBorder +
              " " +
              t.surfaceSoft +
              " " +
              t.textMuted +
              " " +
              t.surfaceHover
            }
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className="flex items-center gap-1.5 rounded-full px-5 py-2 text-xs sm:text-sm font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
            style={{
              background: BRAND.gradient,
              boxShadow: "0 10px 24px -10px rgba(239,93,168,0.55)",
            }}
          >
            {sending ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <Send size={14} />
                Send
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function UserActivityTable({ code }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const USER_ACTIVITY_PAGE_SIZE = 10;

  // reminder state (moved here from EngagementRetention)
  const [reminderRow, setReminderRow] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [sentIds, setSentIds] = useState({});
   const [toast, setToast] = useState(null);

  // inactivity threshold + full user list (for "Remind all")
  const [threshold, setThreshold] = useState(7);
  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    fetchAllActivityUsers(code)
      .then((list) => {
        if (!cancelled) setAllUsers(list);
      })
      .catch((err) => console.log(err));
    return () => {
      cancelled = true;
    };
  }, [code]);

  const isInactive = (u) => {
    const d = daysSince(u?.stats?.lastChatAt);
    return d == null || d >= threshold;
  };

  const inactive = useMemo(
    () => allUsers.filter(isInactive),
    [allUsers, threshold],
  );

  const getEmail = (u) => u?.email || u?.label || "";

  const recipients = useMemo(() => {
    if (!reminderRow) return [];
    const list = reminderRow.bulkCount ? inactive : [reminderRow];
    return [...new Set(list.map(getEmail).filter(Boolean))];
  }, [reminderRow, inactive]);

  const handleSend = async (row, email) => {
    if (sendingId) return;

    const bulk = !!row.bulkCount;
    const targets = bulk ? inactive : [row];
    const emails = [...new Set(targets.map(getEmail).filter(Boolean))];

    if (emails.length === 0) {
      setToast({
        type: "error",
        message: "No email address found for selected user(s).",
      });
      return;
    }

    setSendingId(row.userId);
    try {
      const payload = {
        emails,
        sendEmail: true,
        emailSubject: email.subject,
        emailBody: email.body,
        bodyBelowButton: email.bodyBelowButton,
        ctaText: email.ctaText,
        ctaLink: email.ctaLink,
        scheduleTime: email.scheduleTime
          ? dayjs(email.scheduleTime).toISOString()
          : null,
      };

      const res = await fetch(`${Mail_BASE_URl}/dashboard/send-test-emails`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Request failed");

      setSentIds((p) => {
        const next = { ...p };
        targets.forEach((u) => (next[u.userId] = true));
        return next;
      });

      setToast({
        type: "success",
        message: bulk
          ? `Reminder sent to ${targets.length} users`
          : `Reminder sent to ${row.alias || row.label || "user"}`,
      });
      setReminderRow(null);
    } catch (err) {
      console.error("Send failed:", err);
      setToast({
        type: "error",
        message: err.message || "Couldn't send reminder. Try again.",
      });
    } finally {
      setSendingId(null);
    }
  };
  // new org code -> back to page 1
  useEffect(() => {
    setPage(1);
  }, [code]);

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const res = await fetch(USER_ACTIVITY_URL(code, page));
        const json = await res.json();
        if (cancelled) return;
        const d = json?.data;
        setUsers(d?.users ?? []);
        setTotalPages(d?.totalPages ?? 1);
        setTotalUsers(d?.totalUsers ?? 0);
        if (d?.page && d.page !== page) setPage(d.page);
        setError(false);
      } catch (err) {
        if (!cancelled) {
          console.log(err);
          setError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [code, page]);

  const gridCols = "md:grid-cols-[1.3fr_1fr_1fr_0.8fr_0.9fr_0.9fr_0.9fr]";
  const headCell =
    "text-[11px] font-semibold uppercase tracking-wide " + t.textFaint;
  const emptyBox =
    "rounded-2xl px-4 py-6 text-center text-sm " +
    t.surfaceSoft +
    " " +
    t.textMuted;

  return (
    <>
      <CardShell accent="teal">
                <div className="mb-4 sm:mb-5 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className={"text-lg sm:text-xl font-bold " + t.textPrimary}>
              User Activity
            </h2>
            <p className={"mt-1 text-xs sm:text-sm " + t.textMuted}>
              Onboarding, usage and engagement for every team member
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <label
              className={
                "flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] sm:text-xs font-medium " +
                t.pillBorder +
                " " +
                t.surfaceSoft +
                " " +
                t.textMuted
              }
            >
              Inactive after
              <select
                value={threshold}
                onChange={(e) => setThreshold(Number(e.target.value))}
                className={
                  "bg-transparent outline-none " +
                  t.textPrimary +
                  " [color-scheme:" +
                  (isDark ? "dark" : "light") +
                  "]"
                }
              >
                {THRESHOLD_OPTIONS.map((d) => (
                  <option key={d} value={d}>
                    {d} days
                  </option>
                ))}
              </select>
            </label>

            {inactive.length > 0 && (
              <button
                onClick={() =>
                  setReminderRow({
                    userId: "__bulk__",
                    bulkCount: inactive.length,
                  })
                }
                disabled={!!sendingId}
                className="flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
                style={{ background: BRAND.gradient }}
              >
                <Send size={13} />
                Remind all ({inactive.length})
              </button>
            )}
          </div>
        </div>

        {error ? (
          <div className={emptyBox}>Couldn't load user activity right now.</div>
        ) : loading ? (
          <div className="space-y-2.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <UserActivityRowSkeleton key={i} />
            ))}
          </div>
        ) : users.length === 0 ? (
          <div className={emptyBox}>No users found for this organization.</div>
        ) : (
          <>
            <div className={"hidden md:grid gap-3 px-4 pb-2 " + gridCols}>
              <span className={headCell}>ID</span>
              <span className={headCell}>Onboarding Date</span>
              <span className={headCell}>Last Used</span>
              <span className={headCell}>Messages</span>
              <span className={headCell}>Avg Chat</span>
              <span className={headCell}>Tests Taken</span>
                           <span
                className={
                  headCell + " flex items-center justify-end gap-1.5"
                }
              >
                Action
                <span className="group relative inline-flex">
                  <Info size={13} strokeWidth={2.5} className="cursor-help" />
                  <span
                    role="tooltip"
                    className={
                      "pointer-events-none absolute right-0 top-full z-30 mt-2 hidden w-56 rounded-xl border px-3 py-2 text-[11px] font-medium normal-case leading-relaxed tracking-normal shadow-2xl group-hover:block " +
                      t.card +
                      " " +
                      t.pillBorder +
                      " " +
                      t.textMuted
                    }
                  >
                    The Remind button is shown only for users who have been
                    inactive for {threshold}+ days (or never active).
                  </span>
                </span>
              </span>
            </div>

            <div className="space-y-2.5">
              {users.map((u) => (
                <UserActivityRow
                  key={u.userId}
                  row={u}
                  gridCols={gridCols}
                  onRemind={setReminderRow}
                  sending={sendingId === u.userId}
                  sent={!!sentIds[u.userId]}
                   showRemind={isInactive(u)}
                />
              ))}
            </div>
          </>
        )}

        {!error && totalUsers > 0 && (
          <PaginationBar
            page={page}
            totalPages={totalPages}
            totalUsers={totalUsers}
            limit={USER_ACTIVITY_PAGE_SIZE}
            loading={loading}
            onChange={setPage}
          />
        )}
      </CardShell>

      {reminderRow && (
        <ReminderEmailModal
          row={reminderRow}
          recipients={recipients}
          sending={sendingId === reminderRow.userId}
          onCancel={() => setReminderRow(null)}
          onSend={handleSend}
        />
      )}

      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
}
// ---------------------------------------------------------------------------
// Engagement & Retention — dropout rate, days since last activity,
// weekly active users, and bulk "remind inactive users".
//   Users/last-active come from /userActivity/:code (stats.lastChatAt).
//   Weekly active users come from a NEW endpoint (see USER_ENGAGEMENT_URL).
// ---------------------------------------------------------------------------

const THRESHOLD_OPTIONS = [7, 14, 30];

function daysSince(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.floor(ms / 86400000));
}

// Loads every page of /userActivity so metrics cover the whole org,
// not just the 10 rows shown in the table.
async function fetchAllActivityUsers(code) {
  const all = [];
  let page = 1;
  let totalPages = 1;
  do {
    const res = await fetch(
      `${USER_ACTIVITY_URL(code, page, 50)}?page=${page}`,
    );
    const json = await res.json();
    const d = json?.data;
    all.push(...(d?.users ?? []));
    totalPages = d?.totalPages ?? 1;
    page += 1;
  } while (page <= totalPages && page <= 50);
  return all;
}

function RecipientsField({ recipients, labelCls, fieldBase, error, errCls }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapRef = useRef(null);

  // close on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const copyAll = () => {
    navigator.clipboard?.writeText(recipients.join(", ")).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // No email found / single user: plain read-only field
  if (recipients.length <= 1) {
    return (
      <div>
        <label className={labelCls}>To</label>
        <input
          type="text"
          readOnly
          value={recipients[0] || ""}
          placeholder="No email address found"
          className={fieldBase + " " + (error ? errCls : "")}
        />
      </div>
    );
  }

  // Bulk: trigger + floating popover menu
  return (
    <div ref={wrapRef} className="relative">
      <label className={labelCls}>To</label>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={
          fieldBase + " flex items-center justify-between gap-2 text-left"
        }
      >
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate">{recipients[0]}</span>
          <span
            className={
              "shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold " +
              t.surface +
              " " +
              t.textMuted
            }
          >
            +{recipients.length - 1} more
          </span>
        </span>
        <ChevronDown
          size={16}
          className={
            "shrink-0 transition-transform " +
            t.textMuted +
            (open ? " rotate-180" : "")
          }
        />
      </button>

      {open && (
        <div
          role="menu"
          className={
            "absolute left-0 right-0 top-full z-30 mt-2 overflow-hidden rounded-2xl border backdrop-blur-xl shadow-2xl " +
            t.card +
            " " +
            t.pillBorder
          }
        >
          <ul className="max-h-52 overflow-y-auto py-1.5">
            {recipients.map((e) => (
              <li
                key={e}
                role="menuitem"
                className={
                  "flex items-center gap-2.5 px-4 py-2 text-sm " +
                  t.textPrimary +
                  " " +
                  t.surfaceHover
                }
              >
                <Check
                  size={14}
                  strokeWidth={3}
                  className="shrink-0"
                  style={{ color: BRAND.teal }}
                />
                <span className="truncate">{e}</span>
              </li>
            ))}
          </ul>

          <div className={"border-t " + t.pillBorder}>
            <button
              type="button"
              onClick={copyAll}
              className={
                "flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm font-medium " +
                t.textMuted +
                " " +
                t.surfaceHover
              }
            >
              {copied ? (
                <CheckCircle2 size={14} style={{ color: BRAND.teal }} />
              ) : (
                <Copy size={14} />
              )}
              {copied ? "Copied" : "Copy all addresses"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
function EngagementStat({ label, value, sub, icon: Icon, tint, accent }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  return (
    <CardShell accent={accent}>
      <div className="mb-3 flex items-center justify-between">
        <span
          className="flex h-9 w-9 items-center justify-center rounded-full"
          style={{ backgroundColor: `${tint}22`, color: tint }}
        >
          <Icon size={18} strokeWidth={2} />
        </span>
      </div>
      <div
        className={
          "text-xs font-semibold uppercase tracking-wide " + t.textMuted
        }
      >
        {label}
      </div>
      <div
        className={
          "mt-1.5 text-3xl font-extrabold leading-none " + t.textPrimary
        }
      >
        {value}
      </div>
      {sub && <div className={"mt-2 text-xs " + t.textFaint}>{sub}</div>}
    </CardShell>
  );
}

function EngagementRetention({ code }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const isMobile = useIsMobile();

  const [users, setUsers] = useState([]);
  const [weekly, setWeekly] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [weeklyError, setWeeklyError] = useState(false);

  const [threshold, setThreshold] = useState(7);



 

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const [uRes, wRes] = await Promise.allSettled([
        fetchAllActivityUsers(code),
        fetch(USER_ENGAGEMENT_URL(code)).then((r) => r.json()),
      ]);
      if (cancelled) return;

      if (uRes.status === "fulfilled") {
        setUsers(uRes.value);
        setError(false);
      } else {
        console.log(uRes.reason);
        setError(true);
      }

      if (wRes.status === "fulfilled") {
        const w = wRes.value?.data?.weekly ?? [];
        setWeekly(
          w.map((p) => ({ x: shortDate(p.weekStart), users: p.activeUsers })),
        );
        setWeeklyError(false);
      } else {
        console.log(wRes.reason);
        setWeeklyError(true);
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [code]);

  const metrics = useMemo(() => {
    const withDays = users.map((u) => ({
      ...u,
      daysInactive: daysSince(u.stats?.lastChatAt),
    }));
    const total = withDays.length;
    const inactive = withDays
      .filter((u) => u.daysInactive == null || u.daysInactive >= threshold)
      .sort(
        (a, b) => (b.daysInactive ?? Infinity) - (a.daysInactive ?? Infinity),
      );
    const neverActive = withDays.filter((u) => u.daysInactive == null).length;
    const active = total - inactive.length;
    const dropout = total ? Math.round((inactive.length / total) * 100) : 0;
    return { total, active, inactive, neverActive, dropout };
  }, [users, threshold]);

  const { total, active, inactive, neverActive, dropout } = metrics;
  




  const emptyBox =
    "rounded-2xl px-4 py-6 text-center text-sm " +
    t.surfaceSoft +
    " " +
    t.textMuted;

  return (
    <>
      <div className="flex flex-col gap-4 sm:gap-5">
        {/* Header + threshold picker */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className={"text-lg sm:text-xl font-bold " + t.textPrimary}>
              Engagement &amp; Retention
            </h2>
            <p className={"mt-1 text-xs sm:text-sm " + t.textMuted}>
              Who is still engaged, who has dropped off, and who needs a nudge
            </p>
          </div>
          <label
            className={
              "flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] sm:text-xs font-medium " +
              t.pillBorder +
              " " +
              t.surfaceSoft +
              " " +
              t.textMuted
            }
          >
            Inactive after
            <select
              value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value))}
              className={
                "bg-transparent outline-none " +
                t.textPrimary +
                " [color-scheme:" +
                (isDark ? "dark" : "light") +
                "]"
              }
            >
              {THRESHOLD_OPTIONS.map((d) => (
                <option key={d} value={d}>
                  {d} days
                </option>
              ))}
            </select>
          </label>
        </div>

        {error ? (
          <CardShell accent="teal">
            <div className={emptyBox}>
              Couldn't load engagement data right now.
            </div>
          </CardShell>
        ) : (
          <>
            {/* KPI tiles */}
            <div className="grid grid-cols-1 gap-4 sm:gap-5 sm:grid-cols-3">
              <EngagementStat
                label={`Active (< ${threshold}d)`}
                value={loading ? "…" : active}
                sub={loading ? "" : `of ${total} users`}
                icon={Users}
                tint={BRAND.teal}
                accent="teal"
              />
              <EngagementStat
                label="Dropout Rate"
                value={loading ? "…" : `${dropout}%`}
                sub={
                  loading
                    ? ""
                    : `${inactive.length} inactive ${threshold}+ days`
                }
                icon={UserX}
                tint={BRAND.pink}
                accent="pink"
              />
              <EngagementStat
                label="Never Active"
                value={loading ? "…" : neverActive}
                sub="Onboarded but never chatted"
                icon={Clock}
                tint={BRAND.gold}
                accent="purple"
              />
            </div>

            {/* Weekly active users */}
            <CardShell accent="purple">
              <h3 className={"text-base sm:text-lg font-bold " + t.textPrimary}>
                Weekly Active Users
              </h3>
              <p className={"mt-1 mb-4 text-xs sm:text-sm " + t.textMuted}>
                Unique users who chatted each week
              </p>
              {loading ? (
                <div
                  className={
                    "h-56 sm:h-64 animate-pulse rounded-2xl " + t.surfaceSoft
                  }
                />
              ) : weeklyError || weekly.length === 0 ? (
                <div className={emptyBox}>
                  {weeklyError
                    ? "Weekly activity isn't available yet."
                    : "Not enough activity yet to plot weekly users."}
                </div>
              ) : (
                <div className="h-56 sm:h-64 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={weekly}
                      margin={{
                        top: 10,
                        right: isMobile ? 0 : 10,
                        left: isMobile ? -28 : -10,
                        bottom: 0,
                      }}
                    >
                      <defs>
                        <linearGradient
                          id="wauGrad"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#a78bfa"
                            stopOpacity={1}
                          />
                          <stop
                            offset="100%"
                            stopColor="#8b7cf6"
                            stopOpacity={0.25}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="4 6"
                        stroke={t.gridStroke}
                        vertical={false}
                      />
                      <XAxis
                        dataKey="x"
                        axisLine={false}
                        tickLine={false}
                        tick={{
                          fill: t.axisTick,
                          fontSize: isMobile ? 10 : 12,
                        }}
                        interval={isMobile ? "preserveStartEnd" : 0}
                        dy={8}
                      />
                      <YAxis
                        allowDecimals={false}
                        axisLine={false}
                        tickLine={false}
                        width={isMobile ? 28 : 36}
                        tick={{
                          fill: t.axisTick,
                          fontSize: isMobile ? 10 : 11,
                        }}
                      />
                      <Tooltip
                        formatter={(v) => [v, "Active users"]}
                        labelFormatter={(l) => `Week of ${l}`}
                        contentStyle={{
                          backgroundColor: t.tooltipBg,
                          border: `1px solid ${t.tooltipBorder}`,
                          borderRadius: 16,
                          fontSize: 12,
                        }}
                        labelStyle={{ color: t.axisTick }}
                        cursor={{
                          fill: isDark
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(15,23,42,0.04)",
                        }}
                      />
                      <Bar
                        dataKey="users"
                        fill="url(#wauGrad)"
                        radius={[8, 8, 0, 0]}
                        maxBarSize={isMobile ? 16 : 28}
                        animationDuration={500}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardShell>

            
          </>
        )}
      </div>

     

     
    </>
  );
}
// ---------------------------------------------------------------------------
// Section — fetches real mood data and renders the whole page
// ---------------------------------------------------------------------------

export default function Mood() {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const { code } = useAuth();
  const isAIIMS = code === "AIIMS";

  const [raw, setRaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [viewMode, setViewMode] = useState("overall"); // "overall" | "single"
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState("");

  const isSingle = isAIIMS && viewMode === "single";
  const activeUserId = isSingle ? selectedUserId : null;
  const ready = !isSingle || !!selectedUserId;
  // load user list once, the first time "Individual" is opened
  useEffect(() => {
    if (!isSingle || !code || members.length > 0) return;
        const loadMembers = async () => {
      try {
        setMembersLoading(true);
        const res = await fetch(MOOD_USERS_URL(code));
        const json = await res.json();
        const base = json?.data?.users ?? [];

        // real names/emails live in /userActivity
        let activity = [];
        try {
          activity = await fetchAllActivityUsers(code);
        } catch (e) {
          console.log(e);
        }
        const byId = new Map(activity.map((u) => [String(u.userId), u]));

        const list = base.map((m) => {
          const a = byId.get(String(m.userId));
          return {
            ...m,
            displayName:
              a?.alias || a?.email || m.alias || m.email || m.label,
          };
        });

        setMembers(list);
        if (list.length > 0)
          setSelectedUserId((prev) => prev || list[0].userId);
      } catch (err) {
        console.log(err);
      } finally {
        setMembersLoading(false);
      }
    };
    loadMembers();
  }, [isSingle, code]);

  useEffect(() => {
    if (!code) return;
    if (isSingle && !selectedUserId) {
      setRaw(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setRaw(null); // avoid flashing the previous user's/org's data
        const res = await moodFetch(code, MOOD_DETAILS_URL(code, activeUserId));
        const json = await res.json();
        if (cancelled) return;
        setRaw(json);
        setError(false);
      } catch (err) {
        if (!cancelled) {
          console.log(err);
          setError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [code, isSingle, selectedUserId]);

  const { daily, monthly } = useMemo(() => {
    const bucket = raw?.data?.result?.[0] ?? {};
    const d = (bucket.daily ?? [])
      .slice()
      .sort((a, b) => a.date.localeCompare(b.date));
    const m = (bucket.monthly ?? [])
      .slice()
      .sort((a, b) => a.month.localeCompare(b.month));
    return { daily: d, monthly: m };
  }, [raw]);

  const todayEntry = daily[daily.length - 1];
  const yesterdayEntry = daily[daily.length - 2];

  // ignore gap-filled days (hasData === false) so "no data" doesn't count as 0%
  const scoreOf = (e) =>
    e && e.hasData !== false ? e.organizationAvg : undefined;

  const todayScore = scoreOf(todayEntry);
  const yesterdayScore = scoreOf(yesterdayEntry);

  const last7Valid = daily.slice(-7).filter((e) => e.hasData !== false);
  const thisWeekAvg = last7Valid.length
    ? last7Valid.reduce((s, e) => s + e.organizationAvg, 0) / last7Valid.length
    : undefined;

  const monthEntry = monthly[monthly.length - 1];
  const moodOptions = [
    {
      label: "Today",
      sublabel: todayEntry ? shortDate(todayEntry.date) : "",
      value: todayScore,
    },
    {
      label: "Yesterday",
      sublabel: yesterdayEntry ? shortDate(yesterdayEntry.date) : "",
      value: yesterdayScore,
    },
    { label: "This Week", sublabel: "Avg", value: thisWeekAvg },
  ];

  const weekly = useMemo(() => buildWeeklyComparison(daily), [daily]);
  const monthlyComparison = useMemo(
    () => buildMonthlyComparison(monthly),
    [monthly],
  );

  return (
    <div
      className={
        "min-h-full w-full flex flex-col gap-5 sm:gap-6 " +
        t.textPrimary +
        " " +
        t.pageWash
      }
    >
      {isAIIMS && (
        <ViewModeBar
          viewMode={viewMode}
          onModeChange={setViewMode}
          members={members}
          membersLoading={membersLoading}
          selectedUserId={selectedUserId}
          onSelectUser={setSelectedUserId}
        />
      )}

      {!ready ? (
        <div
          className={
            "mx-4 sm:mx-6 md:mx-10 rounded-2xl px-4 py-10 text-center text-sm " +
            t.surfaceSoft +
            " " +
            t.textMuted
          }
        >
          {membersLoading
            ? "Loading users…"
            : "Select a user to view their insights."}
        </div>
      ) : (
        <>
          <div
            className={
              "grid grid-cols-1 gap-4 sm:gap-5 px-4 sm:px-6 md:px-10 md:grid-cols-3 w-full " +
              (!isAIIMS ? "pt-6 md:pt-10" : "")
            }
          >
            <AvgScoreCard
              label="Monthly Average"
              sublabel="This Month"
              score={monthEntry?.organizationAvg}
              icon={CalendarDays}
              tint={BRAND.purple}
              accent="purple"
            />
            <AvgScoreCard
              label="Today's Average"
              sublabel="Today"
              score={todayScore}
              icon={Gauge}
              tint={BRAND.teal}
              accent="teal"
            />
            <MoodStatisticsCard options={moodOptions} />
          </div>

          <div className="px-4 sm:px-6 md:px-10">
            <PatternsWeNoticed
              key={activeUserId ?? "overall"}
              code={code}
              userId={activeUserId}
            />
          </div>

          <div className="px-4 sm:px-6 md:px-10">
            <ProgressOfWellbeing
              key={activeUserId ?? "overall"}
              weekly={weekly}
              monthly={monthlyComparison}
              daily={daily}
              isSingle={isSingle}
            />
          </div>
          {isAIIMS && !isSingle && (
            <>
              <div className="px-4 sm:px-6 md:px-10">
                <UserActivityTable code={code} />
              </div>
              <div className="px-4 sm:px-6 md:px-10">
                <EngagementRetention code={code} />
              </div>
            </>
          )}
        </>
      )}

      {error && (
        <div
          className={"px-4 sm:px-6 md:px-10 text-center text-xs " + t.textFaint}
        >
          Couldn't reach the mood data service — showing whatever loaded
          successfully.
        </div>
      )}
      {loading && !raw && ready && (
        <div
          className={"px-4 sm:px-6 md:px-10 text-center text-xs " + t.textFaint}
        >
          Loading mood insights…
        </div>
      )}

      <div
        className={`mt-2 flex flex-wrap items-center justify-center gap-1 px-4 pb-8 text-center text-xs ${t.textFaint}`}
      >
        <span>Made with</span>
        <span style={{ color: BRAND.pink }}>💗</span>
        <span>
          by Infiheal — private, judgment-free insights for a healthier
          organization.
        </span>
      </div>
    </div>
  );
}
